import React, { useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  VStack,
  Text,
  Checkbox,
  CheckboxGroup,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { axiosWrapper } from '../../hooks/axios';

interface Classmate {
  pk: string;
  fullName: string;
}

interface GroupWorkCheckInModalProps {
  isOpen: boolean;
  onClose: () => void;
  classmates: Classmate[];
}

// Temporary feature for a single pilot cohort - will be killed or rewritten.
// No close button / overlay-click / esc dismiss on purpose - the student must
// answer (submit a selection, or say they're working alone) to close this.
export const GroupWorkCheckInModal: React.FC<GroupWorkCheckInModalProps> = ({
  isOpen,
  onClose,
  classmates,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const [submittingAction, setSubmittingAction] = useState<'submit' | 'alone' | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const submit = async (groupmatePks: string[], action: 'submit' | 'alone') => {
    setSubmittingAction(action);
    try {
      await axiosWrapper('/api/v1/records/group_work_checkin/', 'post', {
        groupmate_pks: groupmatePks,
      });
    } catch {
      // Best-effort - this is a temporary, non-critical data collection feature.
    } finally {
      setSubmittingAction(null);
      setIsConfirmOpen(false);
      onClose();
    }
  };

  const selectedNames = classmates
    .filter((classmate) => selected.includes(classmate.pk))
    .map((classmate) => classmate.fullName);

  return (
    <Modal isOpen={isOpen} onClose={() => {}} size="md" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="2xl"
        boxShadow="2xl"
        width="90%"
        maxW="500px"
        maxH="calc(100vh - 100px)"
        overflowY="auto"
        top="50px"
        margin={5}
      >
        <ModalHeader pt={6} pb={3}>
          <Text fontSize="xl" fontWeight="bold" color="#2d3748">
            {t('groupWorkCheckin.title')}
          </Text>
          <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={1}>
            {t('groupWorkCheckin.description')}
          </Text>
        </ModalHeader>

        <ModalBody py={5}>
          <Button
            variant="outline"
            onClick={() => submit([], 'alone')}
            isLoading={submittingAction === 'alone'}
            isDisabled={selected.length > 0 || submittingAction !== null}
            loadingText={t('groupWorkCheckin.submitting')}
            w="full"
            borderRadius="xl"
            mb={5}
          >
            {t('groupWorkCheckin.workingAlone')}
          </Button>

          {classmates.length === 0 ? (
            <Text color="gray.500">{t('groupWorkCheckin.noOthers')}</Text>
          ) : (
            <CheckboxGroup value={selected} onChange={(values) => setSelected(values as string[])}>
              <VStack align="start" spacing={3}>
                {classmates.map((classmate) => (
                  <Checkbox key={classmate.pk} value={classmate.pk}>
                    {classmate.fullName}
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          )}
        </ModalBody>

        <ModalFooter pt={3} pb={6}>
          <Button
            colorScheme="blue"
            onClick={() => setIsConfirmOpen(true)}
            isDisabled={selected.length === 0 || submittingAction !== null}
            w="full"
            borderRadius="xl"
          >
            {t('groupWorkCheckin.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsConfirmOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl" mx={5}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('groupWorkCheckin.confirmTitle')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={2}>{t('groupWorkCheckin.confirmDescription')}</Text>
              <VStack align="start" spacing={1}>
                {selectedNames.map((name) => (
                  <Text key={name} fontWeight="semibold">
                    {name}
                  </Text>
                ))}
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="outline"
                onClick={() => setIsConfirmOpen(false)}
                isDisabled={submittingAction !== null}
                borderRadius="xl"
              >
                {t('groupWorkCheckin.confirmCancel')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => submit(selected, 'submit')}
                isLoading={submittingAction === 'submit'}
                loadingText={t('groupWorkCheckin.submitting')}
                ml={3}
                borderRadius="xl"
              >
                {t('groupWorkCheckin.confirmConfirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default GroupWorkCheckInModal;
